import React, { ChangeEvent, useCallback } from "react";

import dayjs from "dayjs";
import customParse from "dayjs/plugin/customParseFormat";

import {
    Control,
    FieldPath,
    FieldValues,
    PathValue,
    useController,
} from "react-hook-form";

import { css } from "@emotion/react";
import styled from "@emotion/styled";

import colors, { blue } from "../../../../utils/colors";
import { IconPredefined } from "../../icons/Icon";
import { PredefinedIconType } from "../../icons/Icon/Predefined";
import { withField as withFieldWrapper } from "../Field";
import FormError from "../FormError";
import icon from "../NewSelect/icon.svg";
import ErrorMessage from "../ReactHookInput/ErroMessage";

dayjs.extend(customParse);

const Wrapper = styled.div`
    display: flex;
    flex-direction: column;
`;

const Container = styled.div`
    position: relative;
    margin-top: 5px;
    margin-bottom: 5px;

    &::after {
        position: absolute;
        top: 12px;
        right: 10px;
        z-index: 10;
        width: 16px;
        height: 16px;
        pointer-events: none;
        content: "";
        background-image: url("${icon}");
        transform: rotate(90deg);
    }
`;

const Placeholder = styled.label`
    position: absolute;
    top: 2px;
    left: 11px;
    display: flex;
    align-items: center;
    height: 100%;
    overflow: visible !important;
    font-size: 1rem;
    color: ${colors.darkGrey};
    pointer-events: none;
    transition: 0.2s ease all;
`;

const Select = styled.select`
    width: 100%;
    max-width: 100%;
    padding: 15px 35px 5px 10px;
    font-size: 1em;
    line-height: 20px;
    color: ${colors.blue};
    background-color: white;
    border-color: ${colors.darkGrey};
    border-style: solid;
    border-width: 1px;
    border-radius: 4px;
    appearance: none;

    &:focus ~ ${Placeholder} {
        align-items: flex-start;
        font-size: 0.75em;
    }

    &:not(:placeholder-shown) ~ ${Placeholder} {
        align-items: flex-start;
        font-size: 0.75em;
    }

    &:hover {
        cursor: pointer;
    }
`;

const IconContainer = styled.div`
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    display: flex;
    align-items: center;
    padding-right: 10px;
`;

export type SelectOption<
    fvs extends FieldValues = FieldValues,
    name extends FieldPath<fvs> = FieldPath<fvs>
> = {
    value: PathValue<fvs, name>;
    text: string;
};

export type SelectOptions<
    fvs extends FieldValues = FieldValues,
    name extends FieldPath<fvs> = FieldPath<fvs>
> = ReadonlyArray<SelectOption<fvs, name>>;

export type iInputProps<
    fvs extends FieldValues = FieldValues,
    name extends FieldPath<fvs> = FieldPath<fvs>,
    ctx = any
> = {
    fullWidth?: boolean;
    name: name;
    id?: string | null;
    isDisabled?: boolean;
    withField?: boolean;
    maxWidth?: any;
    placeholder: string;
    errors?: any;
    icon?: PredefinedIconType;
    resetField?: any;
    defaultValue?: PathValue<fvs, name>;
    rules?: any;
    disallowEmptySelection?: boolean;
    control: Control<fvs, ctx>;
    options: SelectOptions<fvs, name>;
};

const ReactHookSelect = <
    fvs extends FieldValues = FieldValues,
    name extends FieldPath<fvs> = FieldPath<fvs>,
    ctx = any
>({
    fullWidth = false,
    isDisabled = false,
    withField = false,
    maxWidth = undefined,
    placeholder = "",
    errors,
    icon,
    resetField,
    disallowEmptySelection = false,
    options,
    ...props
}: iInputProps<fvs, name, ctx>) => {
    const {
        field: { value, ...field },
        fieldState,
    } = useController(props);

    const onChange = useCallback(
        ({ target: { selectedIndex } }: ChangeEvent<HTMLSelectElement>) => {
            if ([-1, 0].includes(selectedIndex)) {
                field.onChange(props.defaultValue);
            } else {
                field.onChange(options[selectedIndex - 1].value);
            }
        },
        [field, options, props.defaultValue]
    );

    const component = (
        <Wrapper
            css={css`
                max-width: ${maxWidth ?? "none"};
                ${fullWidth ? "width: 100%;" : ""}
            `}
        >
            <Container>
                <Select
                    disabled={isDisabled}
                    {...{ ...field, onChange }}
                    value={value}
                >
                    <option disabled={disallowEmptySelection}>
                        Bitte wählen
                    </option>
                    {options.map((option) => (
                        <option key={option.value} value={option.value}>
                            {option.text}
                        </option>
                    ))}
                </Select>

                <Placeholder htmlFor={props.name}>{placeholder}</Placeholder>
                {icon && (
                    <IconContainer>
                        <IconPredefined type={icon} size="20px" color={blue} />
                    </IconContainer>
                )}
                {resetField && (
                    <IconContainer>
                        <IconPredefined
                            css={css`
                                cursor: pointer;
                            `}
                            type={PredefinedIconType.delete}
                            size="20px"
                            color={blue}
                            onClick={() =>
                                resetField(props.name, { defaultValue: "" })
                            }
                        />
                    </IconContainer>
                )}
            </Container>
            {fieldState.error && (
                <ErrorMessage
                    error={fieldState.error}
                    name={placeholder}
                    rules={props.rules}
                />
            )}
            {errors && Array.isArray(errors) && (
                <FormError gtag={`error:${props.name}`} message={errors[0]} />
            )}
        </Wrapper>
    );

    return withField ? withFieldWrapper(component) : component;
};
export default ReactHookSelect;
